import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { useFormat } from '../../../../hooks/index';
import { DilutionCondition } from '../../../../types/pool-plans.types';
import { openSelectedCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { RecordAttachedFilesPreview } from '../../components/record-attached-files-preview';
import { ChildrenFormProps } from '../pool-form.types';

export type FinalStepProps = ChildrenFormProps;

export const FinalStep: FC<FinalStepProps> = ({
  handleCloseModal,
  nextFormStep,
  formData,
  prevFormStep,
  isLoading,
}) => {
  const { stepOne, stepTwo } = formData();
  const { format } = useFormat();

  const handleDocumentClick = (link: string) => {
    void openSelectedCompanyStorageUrlInNewTab(link);
  };

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="GENERAL" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="POOL_NAME" />}
              value={stepOne.name}
            />

            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="FULLY_DILUTED_SHARES" />}
              value={
                stepOne.dillutedSharesCount &&
                Number(stepOne.dillutedSharesCount).toLocaleString('en-US')
              }
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SHARE_CLASS" />}
              value={stepOne.shareClass.name}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="DATE" />}
              value={format(stepOne.date, 'dd/MM/yyyy')}
            />
            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="DILUTION_CONDITION" />}
              value={
                stepOne.dilutionCondition === DilutionCondition.INCLUDE_CAP_TABLE ? (
                  <AppFormattedMessage id="INCLUDE_CAP_TABLE" />
                ) : (
                  <AppFormattedMessage id="EXCLUDE_CAP_TABLE" />
                )
              }
            />
          </div>
        </div>
        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className="flex min-w-0 gap-3 overflow-hidden px-4 py-2">
            <span className="w-[120px] shrink-0 text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="DOCUMENTS" />:
            </span>
            <RecordAttachedFilesPreview
              files={stepTwo?.files}
              onDocumentClick={handleDocumentClick}
            />
          </div>
        </div>
        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className="flex w-full gap-3 px-4 py-2">
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div className="max-w-[250px] break-words text-xs font-[450] text-gray-700">
              {stepTwo?.additionalNotes || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>

          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="ADD_POOL" />
          </Button>
        </div>
      </div>
    </>
  );
};
